import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useChatStore } from "../../store/chatStore";
import { decodeTokenService } from "../../services/decodeTokenService";
import { useAuth } from "../../context/AuthContext";
import { dictionary } from "../../utils/dictionary";
import Loader from "../../components/common/Loader";
import ChatSetup from "./components/ChatSetup";
import ChatInbox from "./components/ChatInbox";
import KanbanContent from "../KanbanPage/KanbanContent";
import WSConfigTab from "./components/WSConfigTab";

type ChatPageTab = "chat" | "confirmaciones" | "configuracion-ws";

const VALID_TABS: ChatPageTab[] = ["chat", "confirmaciones", "configuracion-ws"];

const ChatPage = () => {
  const { language } = useAuth();
  const { whatsappAccount, accountLoading, accountChecked, fetchAccount } =
    useChatStore();

  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = VALID_TABS.includes(searchParams.get("tab") as ChatPageTab)
    ? (searchParams.get("tab") as ChatPageTab)
    : "chat";
  const [activeTab, setActiveTab] = useState<ChatPageTab>(initialTab);

  useEffect(() => {
    const ownerId = decodeTokenService.getUserIdFromToken();
    if (ownerId && !accountChecked) {
      fetchAccount(ownerId);
    }
  }, []);

  const handleTabChange = (tab: ChatPageTab) => {
    setActiveTab(tab);
    setSearchParams((p) => {
      if (tab === "chat") {
        p.delete("tab");
      } else {
        p.set("tab", tab);
      }
      return p;
    });
  };

  if (accountLoading || !accountChecked) {
    return (
      <div className="flex justify-center items-center h-screen bg-[#F5F6FA]">
        <Loader />
      </div>
    );
  }

  if (!whatsappAccount || !whatsappAccount.is_active) {
    return <ChatSetup />;
  }

  const tabs: { id: ChatPageTab; label: string }[] = [
    { id: "chat", label: dictionary["chat-tab-chat"]?.[language] ?? "Chat" },
    { id: "confirmaciones", label: dictionary["chat-tab-confirmaciones"]?.[language] ?? "Confirmaciones" },
    { id: "configuracion-ws", label: dictionary["chat-tab-config-ws"]?.[language] ?? "Configuración WS" },
  ];

  return (
    <div className="flex flex-col h-screen bg-[#F5F6FA]">
      {/* Tab bar */}
      <div className="px-6 pt-4 pb-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center bg-gray-200 rounded-full p-1 w-fit">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                activeTab === tab.id
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      {/* Tab content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === "chat" && <ChatInbox />}
        {activeTab === "confirmaciones" && <KanbanContent />}
        {activeTab === "configuracion-ws" && <WSConfigTab />}
      </div>
    </div>
  );
};

export default ChatPage;
